<template>
    <div class="main-content video-portal">
        <div class="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
            <div>
                <h2 class="mb-1">Video Portal</h2>
                <p class="mb-0 text-sm text-pip-gray">Review and manage the videos displayed in Sales Tools.</p>
            </div>
            <router-link class="btn btn-sm btn-danger self-start md:self-auto" :to="{ name: 'video-create' }">
                New Video
            </router-link>
        </div>
        <p class="diagonal-lines my-4"></p>

        <div v-if="deleteSuccessMessage" class="alert alert-success py-2" role="status">
            {{ deleteSuccessMessage }}
        </div>
        <div v-if="deleteError" class="alert alert-danger py-2" role="alert">{{ deleteError }}</div>

        <div v-if="loading" class="flex justify-center items-center py-12">
            <vue-loaders-ball-beat scale="2" color="black" />
        </div>
        <div v-else-if="error" class="alert alert-danger" role="alert">
            <strong>Videos could not be loaded.</strong>
            <span class="block mt-1">Please refresh the page and try again.</span>
        </div>
        <div v-else-if="videos.length === 0" class="video-empty p-8 text-center">
            <p class="mb-1 font-bold text-lg">No video entries found</p>
            <p class="mb-0 text-sm text-pip-gray">Create a video to add it to the Sales Tools page.</p>
        </div>

        <template v-else>
            <section class="video-filters mb-3" aria-label="Video filters">
                <div class="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <div class="video-search w-full">
                        <label for="video-search" class="sr-only">Search videos</label>
                        <input
                            id="video-search"
                            v-model.trim="searchQuery"
                            type="search"
                            class="form-control form-control-sm"
                            placeholder="Search title, source, or thumbnail…"
                        />
                    </div>
                    <button v-if="searchQuery" type="button" class="btn btn-sm btn-secondary flex-none" @click="searchQuery = ''">
                        Clear search
                    </button>
                </div>
            </section>

            <div v-if="filteredVideos.length" class="video-toolbar flex items-center justify-between gap-3 mb-2">
                <p class="mb-0 text-sm text-pip-gray" aria-live="polite">
                    <strong class="text-tertiary">{{ filteredVideos.length }}</strong>
                    {{ filteredVideos.length === 1 ? "item" : "items" }}
                    <span v-if="searchQuery">of {{ videos.length }}</span>
                    · Showing {{ rangeStart }}–{{ rangeEnd }}
                </p>
                <div class="flex items-center gap-2">
                    <label for="video-page-size" class="mb-0 text-xs font-semibold text-pip-gray">Per page</label>
                    <admin-form-field id="video-page-size" v-model="pageSize" :options="pageSizeOptions" class="video-page-size" />
                </div>
            </div>

            <div v-if="filteredVideos.length === 0" class="video-empty p-6 text-center">
                <p class="mb-1 font-bold">No matching videos found</p>
                <p class="mb-3 text-sm text-pip-gray">Try a different search.</p>
                <button type="button" class="btn btn-sm btn-secondary" @click="searchQuery = ''">Clear search</button>
            </div>

            <template v-else>
                <div class="table-responsive video-table-wrap">
                    <table class="table video-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col" class="column-thumb">Thumbnail</th>
                                <th scope="col" class="column-title">Video</th>
                                <th scope="col">Type</th>
                                <th scope="col">Source</th>
                                <th scope="col">Thumbnail source</th>
                            </tr>
                        </thead>
                        <tbody>
                            <video-table-row
                                v-for="video in pageItems"
                                :key="video.id"
                                :video="video"
                                :deleting="deletingId === video.id"
                                @delete="requestDelete"
                            />
                        </tbody>
                    </table>
                </div>
                <div class="video-pagination mt-3">
                    <jw-pagination
                        :key="paginationKey"
                        :items="filteredVideos"
                        :page-size="pageSize"
                        :initial-page="1"
                        :max-pages="7"
                        :disable-default-styles="true"
                        @changePage="onPageChange"
                    />
                </div>
            </template>
        </template>

        <admin-confirm-dialog
            id="portal-delete-video"
            :open="Boolean(deleteCandidate)"
            title="Delete video?"
            :message="deleteConfirmationMessage"
            confirm-label="Delete video"
            working-label="Deleting…"
            :working="Boolean(deletingId)"
            @confirm="deleteVideo"
            @cancel="deleteCandidate = null"
        />
    </div>
</template>

<script>
import JwPagination from "@c/shared/Pagination";
import { AdminConfirmDialog, AdminFormField } from "@c/shared/admin";
import VideoTableRow from "./video/VideoTableRow";

export default {
    name: "VideoPortal",
    components: { AdminConfirmDialog, AdminFormField, JwPagination, VideoTableRow },
    data() {
        return {
            videos: [],
            pageItems: [],
            pageSize: 10,
            currentPage: 1,
            searchQuery: "",
            loading: false,
            error: null,
            deleteCandidate: null,
            deletingId: null,
            deleteError: null,
            deleteSuccessMessage: this.$route.query.deleted === "1" ? "Video deleted successfully." : null,
        };
    },
    computed: {
        pageSizeOptions() {
            return [10, 25, 50, 100].map(value => ({ value, label: String(value) }));
        },
        filteredVideos() {
            const query = this.searchQuery.toLowerCase();
            if (!query) return this.videos;
            return this.videos.filter(video =>
                [video.title, video.src, video.thumb].some(value =>
                    String(value || "").toLowerCase().includes(query)
                )
            );
        },
        paginationKey() {
            return [this.pageSize, this.searchQuery, this.videos.length].join("|");
        },
        rangeStart() {
            return this.pageItems.length ? (this.currentPage - 1) * this.pageSize + 1 : 0;
        },
        rangeEnd() {
            return (this.currentPage - 1) * this.pageSize + this.pageItems.length;
        },
        deleteConfirmationMessage() {
            if (!this.deleteCandidate) return "";
            return `“${this.deleteCandidate.title || "Untitled video"}” will be permanently removed. This cannot be undone.`;
        },
    },
    created() {
        this.loading = true;
        axios
            .get("/api/admin/videos")
            .then(response => { this.videos = _.orderBy(response.data, "id"); })
            .catch(error => { this.error = error; })
            .finally(() => { this.loading = false; });
    },
    methods: {
        onPageChange(pageOfItems) {
            this.pageItems = pageOfItems;
            if (pageOfItems.length && this.filteredVideos.length) {
                const firstIdx = this.filteredVideos.indexOf(pageOfItems[0]);
                this.currentPage = Math.floor(firstIdx / this.pageSize) + 1;
            }
        },
        requestDelete(video) {
            this.deleteError = null;
            this.deleteSuccessMessage = null;
            this.deleteCandidate = video;
        },
        async deleteVideo() {
            if (!this.deleteCandidate || this.deletingId) return;
            const video = this.deleteCandidate;
            this.deletingId = video.id;
            this.deleteError = null;

            try {
                await axios.delete(`/api/admin/videos/${video.id}`);
                this.videos = this.videos.filter(item => item.id !== video.id);
                this.pageItems = this.pageItems.filter(item => item.id !== video.id);
                this.deleteCandidate = null;
                this.deleteSuccessMessage = "Video deleted successfully.";
            } catch (error) {
                this.deleteError = "Video could not be deleted. Please try again.";
            } finally {
                this.deletingId = null;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.video-empty { border: 1px solid var(--color-light-grey); background: #fff; }
.video-search { max-width: 460px; }
.video-page-size { width: auto; min-width: 4.5rem; }
.video-table-wrap { border: 1px solid var(--color-light-grey); }
.video-table { min-width: 850px; color: var(--color-mine-shaft); font-size: var(--font-size-sm); }

.video-table th {
    padding: 0.55rem 0.65rem;
    border-top: 0;
    border-bottom: 1px solid var(--color-light-grey);
    background: var(--color-gallery);
    color: var(--color-mine-shaft);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-bold);
    letter-spacing: var(--tracking-wide);
    text-transform: uppercase;
    vertical-align: middle;
}

.column-thumb { width: 8rem; }
.column-title { width: 30%; }

::v-deep .video-pagination ul {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.25rem;
    margin: 0;
    padding: 0;
    list-style: none;
}

::v-deep .video-pagination a {
    display: flex;
    min-width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    padding: 0 0.5rem;
    border: 1px solid var(--color-light-grey);
    background: #fff;
    color: #01549e;
    font-size: var(--font-size-sm);
}

::v-deep .video-pagination li.active a { border-color: #01549e; background: #01549e; color: #fff; }
::v-deep .video-pagination li.disabled a { color: #aaa; cursor: default; }

@media (max-width: 639px) {
    .video-toolbar { align-items: flex-start; flex-direction: column; }
}
</style>
